import {
  OnboardingSubstatus,
  OptOutReasonV2,
  ReferredOutReason,
} from "@/lib/modules/students/statuses/enums";
import {
  CareSponsorshipEndReason,
  type CareSponsorshipEndReason as CareSponsorshipEndReasonType,
} from "@marble/core/modules/care-sponsorship";
import { StateToStr } from "@marble/core/modules/states/constants";

export { StateToStr };

import {
  AppointmentType,
  ArchiveReason,
  CallOutcome,
  CallType,
  CarePlanStatus,
  CaseStatus,
  CaseType,
  ChurnReason,
  ClinicalClientNeed,
  ClinicalSpecialty,
  ConfirmationSource,
  DosageUnit,
  EducationLevel,
  EngagementVerificationType,
  FormType,
  FrequencyUnit,
  Gender,
  Grade,
  GroupNextSteps,
  GroupStatus,
  HeadwayVerificationStatus,
  HeardAboutMarbleFromType,
  InsuranceCaptureStatus,
  InsuranceClaimNotSubmittedReason,
  InterestAssessmentType,
  Language,
  LGBTQIdentity,
  ManualVerificationStatus,
  MedicationForm,
  Modality,
  NoConversionReason,
  NoteType,
  NotSchedulingReason,
  Race,
  ReferralSource,
  ReferralRiskFactor,
  ReferringForTherapyType,
  Religion,
  SchoolStatus,
  SchoolSubtype,
  SchoolType,
  SchoolWorkerType,
  StudentGroupStatus,
  StudentGuardianRelationType,
  StudentStatus,
  StudentThereapistRelationType,
  SuperviseePaymentTiming,
  SupervisorPaymentModel,
  TaskSubtype,
  TaskType,
  TherapistLicenseType,
  TherapistStatus,
  TherapyType,
  UserType,
  type SIStatusType,
} from "@prisma/client";

export const UserTypeToTitle: Record<UserType, string> = {
  [UserType.ADMIN]: "Marble Admin",
  [UserType.THERAPIST]: "Therapist",
  [UserType.GUARDIAN]: "Guardian",
  [UserType.STUDENT]: "Student",
  [UserType.SCHOOL_WORKER]: "School Worker",
};

export const LanguageStrToEnum: Record<string, Language> = {
  English: Language.ENGLISH,
  "American Sign Language": Language.ASL,
  Arabic: Language.ARABIC,
  Bengali: Language.BENGALI,
  Cantonese: Language.CANTONESE,
  Farsi: Language.FARSI,
  French: Language.FRENCH,
  Greek: Language.GREEK,
  "Haitian Creole": Language.HATIAN_CREOLE,
  Hebrew: Language.HEBREW,
  Hindi: Language.HINDI,
  "Jamaican Patois": Language.JAMAICAN_PATOIS,
  Japanese: Language.JAPANESE,
  Korean: Language.KOREAN,
  Krio: Language.KRIO,
  Malayalam: Language.MALAYALAM,
  Mandarin: Language.MANDARIN,
  Navajo: Language.NAVAJO,
  Portuguese: Language.PORTUGUESE,
  Punjabi: Language.PUNJABI,
  Russian: Language.RUSSIAN,
  Spanish: Language.SPANISH,
  Swahili: Language.SWAHILI,
  Twi: Language.TWI,
  Urdu: Language.URDU,
};

/** Language enum to display string (e.g. for UI labels). For written/text language in translations, use getCopyLanguage from @/lib/language. */
export const LanguageEnumToStr = Object.fromEntries(
  Object.entries(LanguageStrToEnum).map(([str, enumVal]) => [enumVal, str]),
);

export const GradeStrToEnum: Record<string, Grade> = {
  "Pre-school": Grade.PRE_SCHOOL,
  Kindergarten: Grade.KINDERGARTEN,
  "1st": Grade.FIRST,
  "2nd": Grade.SECOND,
  "3rd": Grade.THIRD,
  "4th": Grade.FOURTH,
  "5th": Grade.FIFTH,
  "6th": Grade.SIXTH,
  "7th": Grade.SEVENTH,
  "8th": Grade.EIGHTH,
  "9th": Grade.NINTH,
  "10th": Grade.TENTH,
  "11th": Grade.ELEVENTH,
  "12th": Grade.TWELFTH,
  College: Grade.COLLEGE,
  Other: Grade.OTHER,
  Unknown: Grade.UNKNOWN,
  Graduated: Grade.GRADUATED,
};

export const GradeEnumToStr = Object.fromEntries(
  Object.entries(GradeStrToEnum).map(([str, enumVal]) => [enumVal, str]),
);

export const GradeEnumToInt: Record<Grade, number> = {
  [Grade.PRE_SCHOOL]: -1,
  [Grade.KINDERGARTEN]: 0,
  [Grade.FIRST]: 1,
  [Grade.SECOND]: 2,
  [Grade.THIRD]: 3,
  [Grade.FOURTH]: 4,
  [Grade.FIFTH]: 5,
  [Grade.SIXTH]: 6,
  [Grade.SEVENTH]: 7,
  [Grade.EIGHTH]: 8,
  [Grade.NINTH]: 9,
  [Grade.TENTH]: 10,
  [Grade.ELEVENTH]: 11,
  [Grade.TWELFTH]: 12,
  [Grade.GRADUATED]: 13,
  [Grade.OTHER]: 14,
  [Grade.UNKNOWN]: 15,
  [Grade.COLLEGE]: 16,
};

export const GradeIntToEnum: Record<number, Grade> = {
  [-1]: Grade.PRE_SCHOOL,
  0: Grade.KINDERGARTEN,
  1: Grade.FIRST,
  2: Grade.SECOND,
  3: Grade.THIRD,
  4: Grade.FOURTH,
  5: Grade.FIFTH,
  6: Grade.SIXTH,
  7: Grade.SEVENTH,
  8: Grade.EIGHTH,
  9: Grade.NINTH,
  10: Grade.TENTH,
  11: Grade.ELEVENTH,
  12: Grade.TWELFTH,
  13: Grade.GRADUATED,
  14: Grade.OTHER,
  15: Grade.UNKNOWN,
  16: Grade.COLLEGE,
};

export const SIStatusTypeToStr: Record<SIStatusType, string> = {
  NONE: "None",
  SI_HISTORY: "SI History",
  ACTIVE_SI: "Active SI",
};

export const StudentStatusEnumToStr: Record<StudentStatus, string> = {
  //Mutually exclusive
  [StudentStatus.ONBOARDING]: "Onboarding",
  [StudentStatus.IN_CARE]: "In Care",
  [StudentStatus.CARE_GAP]: "Care Gap",
  [StudentStatus.NON_RESPONSIVE]: "Non-Responsive",
  [StudentStatus.CARE_COMPLETED]: "Care Completed",
  [StudentStatus.OPTED_OUT]: "Opted Out of Care",
  [StudentStatus.REFERRED_OUT]: "Referred Out",

  // Metadata Statuses
  [StudentStatus.FIRST_REFERRAL]: "First Referral",
  [StudentStatus.REFERRED_FOR_ADULT_THERAPY_ONLY]:
    "Referred for Adult Therapy Only",
  [StudentStatus.PAYER_REFERRAL]: "Payer Referral",

  //Deprecated Statuses
  [StudentStatus.REFERRED]: "Referred",
  [StudentStatus.IN_GROUP_THERAPY]: "In Group Therapy",
  [StudentStatus.IN_INDIVIDUAL_THERAPY]: "In Individual Therapy",
  [StudentStatus.IN_FAMILY_THERAPY]: "In Family Therapy",
  [StudentStatus.MEDICATION_MANAGEMENT]: "Medication Management",
};

export const StudentStatusToColor: Record<StudentStatus, string> = {
  //Mutually exclusive
  [StudentStatus.ONBOARDING]: "bg-primary",
  [StudentStatus.IN_CARE]: "bg-green-500",
  [StudentStatus.CARE_GAP]: "bg-yellow-500",
  [StudentStatus.NON_RESPONSIVE]: "bg-gray-500",
  [StudentStatus.CARE_COMPLETED]: "bg-green-700",
  [StudentStatus.OPTED_OUT]: "bg-gray-500",
  [StudentStatus.REFERRED_OUT]: "bg-gray-500",

  // Metadata Statuses
  [StudentStatus.FIRST_REFERRAL]: "bg-purple-400",
  [StudentStatus.PAYER_REFERRAL]: "bg-purple-400",
  [StudentStatus.REFERRED_FOR_ADULT_THERAPY_ONLY]: "bg-gray-500",

  //Deprecated Statuses
  [StudentStatus.REFERRED]: "bg-primary",
  [StudentStatus.IN_FAMILY_THERAPY]: "bg-green-500",
  [StudentStatus.IN_GROUP_THERAPY]: "bg-green-500",
  [StudentStatus.IN_INDIVIDUAL_THERAPY]: "bg-green-500",
  [StudentStatus.MEDICATION_MANAGEMENT]: "bg-blue-500",
};

export const CareSponsorshipEndReasonToStr: Record<
  CareSponsorshipEndReasonType,
  string
> = {
  [CareSponsorshipEndReason.SWITCHED_TO_INSURANCE]: "Switched to insurance",
  [CareSponsorshipEndReason.CAP_REACHED]: "Session cap reached",
  [CareSponsorshipEndReason.CARE_COMPLETED]: "Care completed",
  [CareSponsorshipEndReason.REFERRED_OUT]: "Referred out",
  [CareSponsorshipEndReason.SCHOOL_CHANGE]: "School change",
};

export const OptOutReasonEnumV2ToStr: Record<OptOutReasonV2, string> = {
  [OptOutReasonV2.COPAY]: "Copay",
  [OptOutReasonV2.NO_LONGER_INTERESTED]: "No Longer Interested",
  [OptOutReasonV2.RESISTANCE_TO_VIRTUAL]: "Wants in Person",
  [OptOutReasonV2.ALREADY_IN_CARE]: "Already in Care",
  [OptOutReasonV2.RELOCATED_OUT_OF_SERVICE_AREA]:
    "Relocated out of Service Area",
  [OptOutReasonV2.OTHER]: "Other",
  [OptOutReasonV2.RESISTANCE_TO_THERAPY_GUARDIAN]:
    "Guardian Resistance to Therapy",
  [OptOutReasonV2.RESISTANCE_TO_THERAPY_STUDENT]:
    "Client Resistance to Therapy",

  // Deprecated: kept for backward-compatible display of historical records
  [OptOutReasonV2.ATTENDENCE_POLICY]: "Attendance Policy",
  [OptOutReasonV2.NON_RESPONSIVE]: "Non-Responsive",
  [OptOutReasonV2.NO_LONGER_NEEDS_THERAPY]: "No Longer Needs Therapy",
  [OptOutReasonV2.CARE_CONCLUDED]: "Care Concluded",
  [OptOutReasonV2.RESISTANCE_TO_THERAPY]: "Resistance to Therapy",
  [OptOutReasonV2.RESISTANCE_TO_VIRTUAL_GUARDIAN]:
    "Guardian Resistance to Virtual",
  [OptOutReasonV2.RESISTANCE_TO_VIRTUAL_STUDENT]:
    "Student Resistance to Virtual",
};

export const ReferredOutReasonEnumToStr: Record<ReferredOutReason, string> = {
  [ReferredOutReason.ACUITY]: "Acuity (SI/HI)",
  [ReferredOutReason.SPECIALIZED_CARE]: "Specialized Care",
  [ReferredOutReason.INSURANCE]: "OON Insurance",
  [ReferredOutReason.AGE]: "Age",
  [ReferredOutReason.LANGUAGE]: "Language",
  [ReferredOutReason.OTHER]: "Other",
  [ReferredOutReason.INACTIVE_INSURANCE]: "Inactive Insurance",
  [ReferredOutReason.INELIGIBLE_INSURANCE]: "Ineligible Insurance",
  [ReferredOutReason.HEADWAY_SETUP_INCOMPLETE]: "Headway Setup Incomplete",
  [ReferredOutReason.ATTENDANCE_POLICY]: "Attendance Policy",
  // Deprecated: kept for backward-compatible display of historical records
  [ReferredOutReason.DISCHARGED]: "Discharged",
};

export const OnboardingSubstatusToStr: Record<OnboardingSubstatus, string> = {
  [OnboardingSubstatus.NOT_STARTED]: "Not Started",
  [OnboardingSubstatus.IN_PROGRESS]: "In Progress",
  [OnboardingSubstatus.INTAKE_SCHEDULED]: "Intake Scheduled",
};

export const OnboardingSubstatusToTooltip: Record<OnboardingSubstatus, string> =
  {
    [OnboardingSubstatus.NOT_STARTED]:
      "We have not yet been able to contact the client, and they have not started self-serve onboarding.",
    [OnboardingSubstatus.IN_PROGRESS]:
      "We have been able to contact the client and are working to schedule them for an intake, or they have started self-serve onboarding.",
    [OnboardingSubstatus.INTAKE_SCHEDULED]:
      "Client has an intake appointment scheduled with a therapist.",
  };

// Tooltips shown on the primary status badge for statuses that need explanation.
// Only statuses listed here will show an info icon on the badge.
export const StudentStatusToTooltip: Partial<Record<StudentStatus, string>> = {
  [StudentStatus.CARE_GAP]:
    "This client hasn't attended an appointment in at least 30 days and has no future appointments scheduled. We are reaching out to them to see if they would like to continue care.",
  [StudentStatus.OPTED_OUT]: "Client opted out of care.",
  [StudentStatus.REFERRED_OUT]: "Marble referred the student out of care.",
};

// Moved to `@marble/core` so the no-show-fee cron can name appointment types
// in the task notes it writes; re-exported here because the app reaches for it
// through `@/lib/enumMappings`.
export { AppointmentTypeEnumToStr } from "@marble/core/modules/appointments/constants";

export const AppointmentTypeEnumToStrClientFacing: Record<
  AppointmentType,
  string
> = {
  [AppointmentType.CONSULT]: "Consult",
  [AppointmentType.INTAKE]: "Intake Session",
  [AppointmentType.INDIVIDUAL_THERAPY]: "Session",
  [AppointmentType.FAMILY_THERAPY]: "Family Session",
  [AppointmentType.GUARDIAN_CONSULT]: "Guardian Consult",
  [AppointmentType.GROUP_THERAPY]: "Group Session",
  [AppointmentType.GROUP_CONSULT]: "Group Leader Meet & Greet",
  [AppointmentType.MEDICATION_MANAGEMENT]: "Medication Management Session",
  [AppointmentType.MEDICATION_MANAGEMENT_INTAKE]:
    "Medication Management Intake Session",
};

export const SuperviseePaymentTimingToStr: Record<
  SuperviseePaymentTiming,
  string
> = {
  [SuperviseePaymentTiming.AFTER_FINALIZATION]: "After Finalization",
  [SuperviseePaymentTiming.AFTER_SUPERVISOR_REVIEW]: "After Supervisor Review",
};

export const SupervisorPaymentModelToStr: Record<
  SupervisorPaymentModel,
  string
> = {
  [SupervisorPaymentModel.MONTHLY_FLAT_FEE]: "Monthly Flat Fee",
  [SupervisorPaymentModel.PER_MEETING_AND_APPOINTMENT]:
    "Per Meeting and Appointment",
};

export const TherapistLicenseTypeEnumToStr: Record<
  TherapistLicenseType,
  string
> = {
  [TherapistLicenseType.LCSW]: "LCSW",
  [TherapistLicenseType.LMSW]: "LMSW",
  [TherapistLicenseType.LMHC]: "LMHC",
  [TherapistLicenseType.LCAT]: "LCAT",
  [TherapistLicenseType.LPC]: "LPC",
  [TherapistLicenseType.LPCC]: "LPCC",
  [TherapistLicenseType.LSW]: "LSW",
  [TherapistLicenseType.LCSWA]: "LCSWA",
  [TherapistLicenseType.LISW]: "LISW",
  [TherapistLicenseType.LICSW]: "LICSW",
  [TherapistLicenseType.LGSW]: "LGSW",
  [TherapistLicenseType.PMHNP]: "PMHNP",
};

export const GenderStrToEnum: Record<string, Gender> = {
  Male: Gender.MALE,
  Female: Gender.FEMALE,
  Nonbinary: Gender.NONBINARY,
  Other: Gender.OTHER,
  Unknown: Gender.UNKNOWN,
};

export const GenderEnumToStr = Object.fromEntries(
  Object.entries(GenderStrToEnum).map(([str, enumVal]) => [enumVal, str]),
);

export const GenderEnumToCasualStr: Record<Gender, string> = {
  [Gender.MALE]: "Boy",
  [Gender.FEMALE]: "Girl",
  [Gender.NONBINARY]: "Nonbinary",
  [Gender.UNKNOWN]: "Unknown Gender",
  [Gender.OTHER]: "Other Gender",
};

export const RaceEnumToStr: Record<Race, string> = {
  [Race.INDIGENOUS]: "American Indian, Alaska Native or Indigenous",
  [Race.EAST_ASIAN]: "East Asian",
  [Race.SOUTH_ASIAN]: "South Asian",
  [Race.SOUTHEAST_ASIAN]: "Southeast Asian",
  [Race.BLACK_OR_AFRICAN_AMERICAN]: "Black or African American",
  [Race.HISPANIC_OR_LATINO]: "Hispanic or Latino",
  [Race.MIDDLE_EASTERN_NORTH_AFRICAN]: "Middle Eastern North African",
  [Race.NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER]:
    "Native Hawaiian or Other Pacific Islander",
  [Race.WHITE]: "White",
};

export const UserTypeToNoteType: Record<UserType, NoteType> = {
  [UserType.ADMIN]: NoteType.ADMIN,
  [UserType.SCHOOL_WORKER]: NoteType.SCHOOL_WORKER,
  [UserType.THERAPIST]: NoteType.GENERAL,
  // students & guardians can't create notes rn
  [UserType.STUDENT]: NoteType.GENERAL,
  [UserType.GUARDIAN]: NoteType.GENERAL,
};

export const ReferralSourceStringToEnum: Record<string, ReferralSource> = {
  Guardian: ReferralSource.GUARDIAN,
  Therapist: ReferralSource.THERAPIST,
  "Insurance Payer": ReferralSource.INSURANCE_PAYER,
  "School Worker": ReferralSource.SCHOOL_WORKER,
  Self: ReferralSource.SELF,
  "Pediatrician / Primary Care Provider": ReferralSource.PRIMARY_CARE_PROVIDER,
  Other: ReferralSource.OTHER,
};

export const ReferralSourceEnumToStr = Object.fromEntries(
  Object.entries(ReferralSourceStringToEnum).map(([str, enumVal]) => [
    enumVal,
    str,
  ]),
);

export const HeadwayVerificationStatusToString: Record<
  HeadwayVerificationStatus,
  string
> = {
  [HeadwayVerificationStatus.NEEDS_SUBMISSION_TO_HEADWAY]:
    "Needs Marble Headway Setup",
  [HeadwayVerificationStatus.AWAITING_CLIENT_HEADWAY_SETUP]:
    "Awaiting Client Headway Setup",
  [HeadwayVerificationStatus.HEADWAY_ERROR]: "Headway Error",
  [HeadwayVerificationStatus.HEADWAY_UNBILLABLE_APPOINTMENT]:
    "Headway Unbillable Appointment",
  [HeadwayVerificationStatus.COMPLETE_ELIGBLE]: "Eligible",
  [HeadwayVerificationStatus.COMPLETE_INELIGIBLE]: "Ineligible",
  [HeadwayVerificationStatus.OON]: "Out of Network",
  [HeadwayVerificationStatus.HEADWAY_MANUAL_VERIFICATION]:
    "HW Manual Verification",
};

export const ManualVerificationStatusToString: Record<
  ManualVerificationStatus,
  string
> = {
  [ManualVerificationStatus.ELIGIBLE]: "Eligible",
  [ManualVerificationStatus.INELIGIBLE]: "Ineligible",
  [ManualVerificationStatus.NOT_SUPPORTED]: "Not Supported",
};

export const InsuranceCaptureStatusToString: Record<
  InsuranceCaptureStatus,
  string
> = {
  [InsuranceCaptureStatus.INCOMPLETE]: "Incomplete",
  [InsuranceCaptureStatus.COMPLETE]: "Complete",
  [InsuranceCaptureStatus.ACTION_NEEDED]: "Action Needed",
};

export const StudentGuardianRelationTypeStringToEnum: Record<
  string,
  StudentGuardianRelationType
> = {
  Child: StudentGuardianRelationType.CHILD,
  "Foster Child": StudentGuardianRelationType.FOSTER_CHILD,
  Grandchild: StudentGuardianRelationType.GRAND_CHILD,
  Stepchild: StudentGuardianRelationType.STEP_CHILD,
  "Niece/Nephew": StudentGuardianRelationType.NIECE_OR_NEPHEW,
  Sibling: StudentGuardianRelationType.SIBLING,
  Other: StudentGuardianRelationType.OTHER,
  Unknown: StudentGuardianRelationType.UNKNOWN,
  Self: StudentGuardianRelationType.SELF,
};

// Re-exported so the app's existing import path keeps working; the map itself
// lives in core, where the SMS participant labelling also needs it.
export { StudentGuardianRelationTypeEnumToReverseString } from "@marble/core/modules/students/constants";

export const StudentGuardianRelationTypeEnumToString: Record<
  StudentGuardianRelationType,
  string
> = {
  [StudentGuardianRelationType.CHILD]: "Child",
  [StudentGuardianRelationType.FOSTER_CHILD]: "Foster Child",
  [StudentGuardianRelationType.GRAND_CHILD]: "Grandchild",
  [StudentGuardianRelationType.STEP_CHILD]: "Stepchild",
  [StudentGuardianRelationType.NIECE_OR_NEPHEW]: "Niece/Nephew",
  [StudentGuardianRelationType.SIBLING]: "Sibling",
  [StudentGuardianRelationType.SELF]: "Self",
  [StudentGuardianRelationType.OTHER]: "Other Relation",
  [StudentGuardianRelationType.UNKNOWN]: "Unknown Relation",
};

export const InsuranceClaimNotSubmittedReasonEnumToStr: Record<
  InsuranceClaimNotSubmittedReason,
  string
> = {
  [InsuranceClaimNotSubmittedReason.NO_INSURANCE]: "No Insurance Info",
  [InsuranceClaimNotSubmittedReason.INCOMPLETE_INSURANCE]:
    "Incomplete Insurance Info",
  [InsuranceClaimNotSubmittedReason.INSURANCE_NOT_VERIFIED]:
    "Insurance Not Verified",
  [InsuranceClaimNotSubmittedReason.ERROR]: "Error Submitting Claim",
  [InsuranceClaimNotSubmittedReason.THERAPIST_NOT_INN]: "Therapist not INN",
  [InsuranceClaimNotSubmittedReason.NEEDS_LCSW_REVIEW]: "Needs LCSW Review",
  [InsuranceClaimNotSubmittedReason.SPONSORING_CARE]: "Sponsoring Care",
  [InsuranceClaimNotSubmittedReason.THIRD_PARTY_SPONSORED]:
    "Third Party Sponsored",
  [InsuranceClaimNotSubmittedReason.BELOW_MINIMUM_BILLABLE_TIME]:
    "Below Minimum Billable Time",
  [InsuranceClaimNotSubmittedReason.NOT_IN_PROGRAM]: "Not in Program",
};

export const GroupStatusEnumToStr = {
  [GroupStatus.UPCOMING]: "Upcoming",
  [GroupStatus.ENROLLING]: "Enrolling",
  [GroupStatus.CONFIRMED]: "Confirmed",
  [GroupStatus.ACTIVE]: "Active",
  [GroupStatus.COMPLETED]: "Completed",
  [GroupStatus.ARCHIVED]: "Archived",
};

export const StudentGroupStatusEnumToStr: Record<StudentGroupStatus, string> = {
  [StudentGroupStatus.ACTIVE]: "Active",
  [StudentGroupStatus.COMPLETED]: "Completed",
  [StudentGroupStatus.REMOVED]: "Removed",
  [StudentGroupStatus.OPTED_OUT]: "Opted Out",
};

export const GroupNextStepsEnumToStr: Record<GroupNextSteps, string> = {
  [GroupNextSteps.INDIVIDUAL_THERAPY]: "Continue With Individual Therapy",
  [GroupNextSteps.SAME_GROUP]: "Continue With Group Therapy",
  [GroupNextSteps.DIFFERENT_GROUP]: "Continue With Different Group",
};

export const NoteTypeEnumToStr: Record<NoteType, string> = {
  [NoteType.REFERRAL]: "Referral Note",
  [NoteType.GENERAL]: "Contact Note",
  [NoteType.INTAKE]: "Intake Note",
  [NoteType.ADDITIONAL_INFO_FOR_INTAKE]: "Additional Info for Intake",
  [NoteType.THERAPY]: "Progress Note",
  [NoteType.CASE_MANAGEMENT]: "Case Management",
  [NoteType.THERAPIST_ADDENDUM]: "Therapist Addendum",
  [NoteType.FORM_ADDENDUM]: "Form Addendum",
  [NoteType.THERAPIST_CONTACT]: "Therapist Contact Note",
  [NoteType.SUPERVISION_ADDENDUM]: "Supervision Addendum",
  [NoteType.ADMIN]: "Admin Note",
  [NoteType.SCHOOL_WORKER]: "School Worker Note",
  [NoteType.SCHOOL_WORKER_PROGRESS_NOTE]: "Progress Note",
  [NoteType.CLIENT_OUTREACH]: "Client Outreach Note",
  [NoteType.REFERRAL_ONBOARDING_DROPOFF]: "Referral Onboarding Dropoff",
  [NoteType.REFERRAL_NON_RESPONSIVE]: "Referral Non-Responsive",
  [NoteType.INACTIVE_INSURANCE]: "Inactive Insurance Note",
  [NoteType.INELIGIBLE_INSURANCE]: "Ineligible Insurance Note",
  [NoteType.COUNSELOR_FIRST_FOLLOWUP]: "Counselor First Followup",
  [NoteType.COUNSELOR_SECOND_FOLLOWUP]: "Counselor Second Followup",
  [NoteType.STUDENT_MARTY_NOTE]: "Student Note",
};

export const EducationLevelEnumToStr: Record<EducationLevel, string> = {
  [EducationLevel.EarlyChildhood]: "Early Childhood",
  [EducationLevel.Elementary]: "Elementary",
  [EducationLevel.MiddleSchool]: "Middle School",
  [EducationLevel.HighSchool]: "High School",
  [EducationLevel.Secondary]: "Secondary",
  [EducationLevel.K8]: "K-8",
  [EducationLevel.K12]: "K-12",
  [EducationLevel.Ungraded]: "Ungraded",
};

export const SchoolTypeEnumToStr: Record<SchoolType, string> = {
  [SchoolType.PUBLIC]: "Public",
  [SchoolType.CHARTER]: "Charter",
  [SchoolType.PRIVATE]: "Private",
};

export const SchoolSubtypeEnumToStr: Record<SchoolSubtype, string> = {
  [SchoolSubtype.SPECIAL_EDUCATION]: "Special Education",
  [SchoolSubtype.VOCATIONAL]: "Vocational",
  [SchoolSubtype.MONTESSORI]: "Montessori",
  [SchoolSubtype.SPECIAL_PROGRAM_EMPHASIS]: "Special Program Emphasis",
  [SchoolSubtype.OTHER]: "Other",
};

export const SchoolStatusEnumToStr: Record<SchoolStatus, string> = {
  [SchoolStatus.OPEN]: "Open",
  [SchoolStatus.CLOSED]: "Closed",
  [SchoolStatus.NEW]: "New",
  [SchoolStatus.UNKNOWN]: "Unknown",
};

export const ReferringForTherapyTypeEnumToStr: Record<
  ReferringForTherapyType,
  string
> = {
  STUDENT: "Student Therapy: Services for Students",
  FAMILY: "Family Therapy: Services for Students, Guardians, and Siblings",
  ADULT: "Adult Therapy: Services for Adults",
};

// Note: whenever adding to this, we need to add the type to the
// salesforce Conctact.Employee Type field picklist options:
// https://velocity-energy-2509.my.salesforce-setup.com/lightning/setup/ObjectManager/Contact/FieldsAndRelationships/00Nfn000001ouXt/view
export const SchoolWorkerTypeEnumToStr: Record<string, string> = {
  [SchoolWorkerType.ASSISTANT_PRINCIPAL]: "Assistant Principal",
  [SchoolWorkerType.ASSISTANT_SUPERINTENDENT]: "Assistant Superintendent",
  [SchoolWorkerType.CLINICAL_SUPERVISOR]: "Clinical Supervisor",
  [SchoolWorkerType.COUNSELOR]: "Counselor",
  [SchoolWorkerType.DEAN]: "Dean",
  [SchoolWorkerType.DIRECTOR_OF_STUDENT_SERVICES]:
    "Director of Student Services",
  [SchoolWorkerType.MENTAL_HEALTH_SPECIALIST]: "Mental Health Specialist",
  [SchoolWorkerType.PARENT_COORDINATOR]: "Parent Coordinator",
  [SchoolWorkerType.PRINCIPAL]: "Principal",
  [SchoolWorkerType.PSYCHOLOGIST]: "Psychologist",
  [SchoolWorkerType.SOCIAL_WORKER]: "Social Worker",
  [SchoolWorkerType.SUPERINTENDENT]: "Superintendent",
  [SchoolWorkerType.TEACHER]: "Teacher",
  [SchoolWorkerType.THERAPIST]: "Therapist",
  [SchoolWorkerType.VICE_PRINCIPAL]: "Vice Principal",
  [SchoolWorkerType.OTHER]: "Other",
};

export const HeardAboutMarbleFromTypeEnumToStr: Record<string, string> = {
  [HeardAboutMarbleFromType.SCHOOL_COUNSELOR]: "School Counselor",
  [HeardAboutMarbleFromType.SCHOOL]: "School",
  [HeardAboutMarbleFromType.PRIMARY_CARE_PROVIDER]:
    "Pediatrician / Primary Care Provider",
  [HeardAboutMarbleFromType.HOSPITAL_ER_DISCHARGE]: "Hospital / ER Discharge",
  [HeardAboutMarbleFromType.COMMUNITY_ORGANIZATION_NONPROFIT]:
    "Community Organization / Nonprofit",
  [HeardAboutMarbleFromType.PARENT_WORKSHOP]: "Parent Workshop",
  [HeardAboutMarbleFromType.WEBSITE]: "Marble Website",
  [HeardAboutMarbleFromType.FRIEND]: "Friend",
  [HeardAboutMarbleFromType.FAMILY_REFERRAL]: "Family Referral",
  [HeardAboutMarbleFromType.FAMILY_REFERRAL_EXPERIMENT]:
    "Family Referral (Experiment)",
  [HeardAboutMarbleFromType.FAMILY_REFERRAL_ADULT]: "Family Referral (Adult)",
  [HeardAboutMarbleFromType.INSURANCE_PAYER]: "Payer Referral",
  [HeardAboutMarbleFromType.TEAM_OR_DEPARTMENT_MEETING]:
    "Team or Department Meeting",
  [HeardAboutMarbleFromType.OTHER]: "Other",
};

export const FormTypeToStr: Record<FormType, string> = {
  [FormType.INTAKE_ASSESSMENT]: "Intake Assessment",
  [FormType.ADULT_INTAKE_ASSESSMENT]: "Adult Intake",
  [FormType.SI_ASSESSMENT]: "SI Assessment",
  [FormType.HI_ASSESSMENT]: "HI Assessment",
  [FormType.ASSESSMENT]: "Assessment",
  [FormType.INTAKE]: "Intake",
  [FormType.PSYCHIATRIC_EVALUATION]: "Psychiatric Evaluation",
  [FormType.PSYCHIATRIC_FOLLOW_UP]: "Psychiatric Follow Up",
};

export const AppointmentTypeToTherapyType: Record<
  AppointmentType,
  TherapyType
> = {
  [AppointmentType.CONSULT]: TherapyType.INDIVIDUAL,
  [AppointmentType.INTAKE]: TherapyType.INTAKE,
  [AppointmentType.INDIVIDUAL_THERAPY]: TherapyType.INDIVIDUAL,
  [AppointmentType.GROUP_CONSULT]: TherapyType.GROUP,
  [AppointmentType.GROUP_THERAPY]: TherapyType.GROUP,
  [AppointmentType.FAMILY_THERAPY]: TherapyType.FAMILY,
  [AppointmentType.GUARDIAN_CONSULT]: TherapyType.FAMILY,
  [AppointmentType.MEDICATION_MANAGEMENT]: TherapyType.MEDICATION_MANAGEMENT,
  [AppointmentType.MEDICATION_MANAGEMENT_INTAKE]:
    TherapyType.MEDICATION_MANAGEMENT,
};

export const TherapyTypeToAppointmentType: Record<
  TherapyType,
  AppointmentType
> = {
  [TherapyType.INTAKE]: AppointmentType.INTAKE,
  [TherapyType.INDIVIDUAL]: AppointmentType.INDIVIDUAL_THERAPY,
  [TherapyType.FAMILY]: AppointmentType.FAMILY_THERAPY,
  [TherapyType.GROUP]: AppointmentType.GROUP_THERAPY,
  [TherapyType.MEDICATION_MANAGEMENT]: AppointmentType.MEDICATION_MANAGEMENT,
};

export const TherapyTypeToStr: Record<TherapyType, string> = {
  [TherapyType.INTAKE]: "Intake",
  [TherapyType.INDIVIDUAL]: "Individual",
  [TherapyType.FAMILY]: "Family",
  [TherapyType.GROUP]: "Group",
  [TherapyType.MEDICATION_MANAGEMENT]: "Medication Management",
};

export const NotSchedulingReasonEnumToStr: Record<NotSchedulingReason, string> =
  {
    [NotSchedulingReason.DISCHARGED]: "Discharged",
    [NotSchedulingReason.NON_RESPONSIVE]: "Non-responsive",
    [NotSchedulingReason.RESISTANCE_TO_THERAPY]: "Resistant to therapy",
    [NotSchedulingReason.RESISTANCE_TO_VIRTUAL]: "Resistant to virtual care",
    [NotSchedulingReason.REQUIRES_SPECIALIZED_CARE]:
      "Requires specialized care",
    [NotSchedulingReason.REQUIRES_DIFFERENT_AVAILABILITY]:
      "Requires different availability",
    [NotSchedulingReason.SEEK_DIFFERENT_THERAPIST]: "Seeks different therapist",
    [NotSchedulingReason.REFERRED_OUT_FOR_INSURANCE]:
      "Referred out — insurance",
    [NotSchedulingReason.CARE_SPONSORSHIP_ENDED]: "Care sponsorship ended",
    [NotSchedulingReason.OTHER]: "Other",
  };

export const TherapistStatusEnumToString: Record<TherapistStatus, string> = {
  [TherapistStatus.INVITED]: "Invited",
  [TherapistStatus.PENDING]: "Pending",
  [TherapistStatus.ACTIVE]: "Active",
  [TherapistStatus.ARCHIVED]: "Archived",
  [TherapistStatus.STATE_LAUNCH_PENDING]: "State Launch Pending",
};

export const ArchiveReasonEnumToStr: Record<ArchiveReason, string> = {
  [ArchiveReason.DROPPED_BEFORE_SIGNING]:
    "Dropped Out Before Signing (offer extended, never signed)",
  [ArchiveReason.CHURNED_BEFORE_CLIENTS]:
    "Churned Before Seeing Clients (onboarded, never saw a client)",
  [ArchiveReason.CHURNED_AFTER_CLIENTS]: "Churned After Seeing Clients",
  [ArchiveReason.TERMINATED]: "Terminated",
};

export const ChurnReasonEnumToStr: Record<ChurnReason, string> = {
  [ChurnReason.SEEKING_W2]: "Looking for W2 Position",
  [ChurnReason.LOW_RATES]: "Low Rates",
  [ChurnReason.HOUR_COMMITMENT]: "Unable to Commit to Hours",
  [ChurnReason.PERSONAL_CIRCUMSTANCES]: "Personal Circumstances",
  [ChurnReason.FELT_UNPREPARED]: "Felt Unprepared",
  [ChurnReason.UNRESPONSIVE]: "Unresponsive",
  [ChurnReason.OTHER]: "Other",
  [ChurnReason.WANTED_W2_OFFERED_1099]: "Wanted W2, we offered 1099",
  [ChurnReason.CASELOAD_HOURS_MISMATCH]: "Caseload/hours expectations mismatch",
  [ChurnReason.SCHEDULE_AVAILABILITY_MISMATCH]:
    "Schedule/availability mismatch",
  [ChurnReason.UNCOMFORTABLE_PEDIATRIC_POPULATION]:
    "Uncomfortable with pediatric/school-based population",
  [ChurnReason.TELEHEALTH_ONLY_CONCERNS]: "Telehealth-only concerns",
  [ChurnReason.CREDENTIALING_PAPERWORK_FRICTION]:
    "Credentialing/paperwork friction",
  [ChurnReason.ONBOARDING_CREDENTIALING_TOO_LONG]:
    "Onboarding/credentialing took too long",
  [ChurnReason.RAMP_TOO_SLOW]:
    "Ramp too slow / not enough client volume offered",
  [ChurnReason.TOOK_ANOTHER_JOB]: "Took another job before ramp",
  [ChurnReason.CHANGED_MIND_POPULATION_FIT]:
    "Changed mind about population/model fit",
  [ChurnReason.FT_ROLE_SCHEDULE_CHANGED]: "FT role schedule changed",
  [ChurnReason.INSUFFICIENT_CASELOAD]:
    "Insufficient caseload/not enough referrals",
  [ChurnReason.PAYMENT_BILLING_ISSUES]: "Payment/billing issues",
  [ChurnReason.ADMIN_DOCUMENTATION_BURDEN]: "Admin/documentation burden",
  [ChurnReason.PLATFORM_TECH_FRUSTRATION]: "Platform/tech frustration",
  [ChurnReason.MOVED_OR_LICENSE_ISSUE]: "Moved out of state/license issue",
  [ChurnReason.RELIABILITY]:
    "Reliability (no-shows, cancellations, unresponsiveness)",
  [ChurnReason.DOCUMENTATION_BILLING_INTEGRITY]:
    "Documentation/record integrity/billing integrity",
  [ChurnReason.CLINICAL_QUALITY]: "Clinical quality concerns",
  [ChurnReason.SAFETY_OR_POLICY_VIOLATION]: "Safety concern/policy violation",
  [ChurnReason.FAILED_BACKGROUND_OR_CREDENTIALING]:
    "Failed background check/credentialing issue",
};

// Therapist onboarding preferences mappings
export const SupportedAppointmentTypeDescriptionsForOnboarding = {
  [AppointmentType.INTAKE]:
    "One-off sessions, separate from your ongoing clients.",
  [AppointmentType.INDIVIDUAL_THERAPY]:
    "Individual sessions with kids ages 6 and up.",
  [AppointmentType.GROUP_THERAPY]:
    "Marble provides an in-depth curriculum for our 3 group topics on anxiety, depression, and social skills.",
  [AppointmentType.FAMILY_THERAPY]:
    "Guardians and kids often attend these sessions together.",
  // Medication Management appointments are omitted intentionally -- NPs only do medication management
};

export const SupportedAppointmentTypeDescriptionsForNPOnboarding = {
  [AppointmentType.MEDICATION_MANAGEMENT]:
    "Sessions to evaluate, prescribe, and monitor psychiatric medication for a student.",
};

export const DisplayedAppointmentTypesToSelectedAppointmentTypes: Partial<
  Record<AppointmentType, AppointmentType[]>
> = {
  ...Object.fromEntries(
    Object.values(AppointmentType).map((type) => [type, [type]]),
  ),
  [AppointmentType.MEDICATION_MANAGEMENT]: [
    AppointmentType.MEDICATION_MANAGEMENT,
    AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
  ],
};

// Define the available modalities for therapist onboarding
export const THERAPIST_ONBOARDING_MODALITIES = [
  { value: "CBT", label: "Cognitive Behavioral Therapy (CBT)" },
  {
    value: "TF_CBT",
    label: "Trauma-Focused Cognitive Behavioral Therapy (TF-CBT)",
  },
  { value: "DBT", label: "Dialectical Behavior Therapy (DBT)" },
  { value: "PLAY_THERAPY", label: "Play Therapy (Tele-Play Therapy)" },
  { value: "PCIT", label: "Parent-Child Interaction Therapy (PCIT)" },
  { value: "SOLUTION_FOCUSED", label: "Solution-Focused Brief Therapy (SFBT)" },
  { value: "MINDFULNESS_BASED", label: "Mindfulness-Based Interventions" },
  { value: "FAMILY_SYSTEMS", label: "Family Systems Therapy" },
  {
    value: "ACT",
    label: "Acceptance and Commitment Therapy (ACT) – youth adaptation",
  },
  {
    value: "ART_THERAPY",
    label: "Art Therapy (Virtual/Guided Creative Expression)",
  },
  {
    value: "EMDR",
    label: "Eye Movement Desensitization and Reprocessing (EMDR)",
  },
] as const;

export const ModalityEnumToStr: Record<Modality, string> = {
  [Modality.CBT]: "Cognitive Behavioral Therapy (CBT)",
  [Modality.TF_CBT]: "Trauma-Focused Cognitive Behavioral Therapy (TF-CBT)",
  [Modality.DBT]: "Dialectical Behavior Therapy (DBT)",
  [Modality.PLAY_THERAPY]: "Play Therapy (Tele-Play Therapy)",
  [Modality.PCIT]: "Parent-Child Interaction Therapy (PCIT)",
  [Modality.SOLUTION_FOCUSED]: "Solution-Focused Brief Therapy (SFBT)",
  [Modality.MINDFULNESS_BASED]: "Mindfulness-Based Interventions",
  [Modality.FAMILY_SYSTEMS]: "Family Systems Therapy",
  [Modality.ACT]: "Acceptance and Commitment Therapy (ACT) – youth adaptation",
  [Modality.ART_THERAPY]: "Art Therapy (Virtual/Guided Creative Expression)",
  [Modality.EMDR]: "Eye Movement Desensitization and Reprocessing (EMDR)",
};

// Define the available clinical specialties for therapist onboarding
export const THERAPIST_ONBOARDING_CLINICAL_SPECIALTIES = [
  { value: "ADOPTION", label: "Adoption" },
  { value: "ANGER_MANAGEMENT", label: "Anger Management" },
  { value: "ANXIETY", label: "Anxiety" },
  { value: "BEHAVIORAL_ISSUES", label: "Behavioral Issues" },
  { value: "BIPOLAR_DISORDER", label: "Bipolar Disorder" },
  { value: "BISEXUAL", label: "Bisexual" },
  { value: "BODY_POSITIVITY", label: "Body Positivity" },
  { value: "CHRONIC_ILLNESS", label: "Chronic Illness" },
  { value: "CODEPENDENCY", label: "Codependency" },
  { value: "COPING_SKILLS", label: "Coping Skills" },
  { value: "DEPRESSION", label: "Depression" },
  { value: "DISSOCIATIVE_DISORDERS", label: "Dissociative Disorders (DID)" },
  { value: "DIVORCE", label: "Divorce" },
  { value: "DOMESTIC_ABUSE", label: "Domestic Abuse" },
  { value: "DOMESTIC_VIOLENCE", label: "Domestic Violence" },
  { value: "DUAL_DIAGNOSIS", label: "Dual Diagnosis" },
  {
    value: "EDUCATION_LEARNING_DISABILITIES",
    label: "Education and Learning Disabilities",
  },
  { value: "EMOTIONAL_DISTURBANCE", label: "Emotional Disturbance" },
  { value: "FAMILY_CONFLICT", label: "Family Conflict" },
  { value: "FAITH_BASED_COUNSELING", label: "Faith-based Counseling" },
  { value: "FOSTER_CARE", label: "Foster Care" },
  { value: "GRIEF", label: "Grief" },
  { value: "INTELLECTUAL_DISABILITY", label: "Intellectual Disability" },
  { value: "IMPULSE_CONTROL_DISORDERS", label: "Impulse Control Disorders" },
  { value: "INFIDELITY", label: "Infidelity" },
  { value: "LESBIAN", label: "Lesbian" },
  { value: "LGBTQ", label: "LGBTQ+" },
  { value: "MOOD_DISORDERS", label: "Mood Disorders" },
  {
    value: "NARCISSISTIC_PERSONALITY",
    label: "Narcissistic Personality (NPD)",
  },
  { value: "NON_BINARY", label: "Non-Binary" },
  { value: "OCD", label: "Obsessive–Compulsive (OCD)" },
  { value: "ODD", label: "Oppositional Defiance (ODD)" },
  { value: "PARENTING", label: "Parenting" },
  { value: "PEER_RELATIONSHIPS", label: "Peer Relationships" },
  { value: "PERSONALITY_DISORDERS", label: "Personality Disorders" },
  { value: "PSYCHOSIS", label: "Psychosis" },
  { value: "PSYCHOTIC_DISORDER", label: "Psychotic Disorder" },
  { value: "QUEER", label: "Queer" },
  { value: "RACIAL_IDENTITY", label: "Racial Identity" },
  { value: "RELATIONSHIP_ISSUES", label: "Relationship Issues" },
  { value: "SCHIZOPHRENIA", label: "Schizophrenia" },
  { value: "SCHOOL_ISSUES", label: "School Issues" },
  { value: "SELECTIVE_MUTISM", label: "Selective Mutism" },
  { value: "SELF_ESTEEM", label: "Self Esteem" },
  { value: "SELF_HARMING", label: "Self-Harming" },
  { value: "SEXUAL_ABUSE", label: "Sexual Abuse" },
  { value: "SEXUAL_TRAUMA", label: "Sexual Trauma" },
  { value: "SPIRITUALITY", label: "Spirituality" },
  { value: "SPORTS_PERFORMANCE", label: "Sports Performance" },
  { value: "STRESS", label: "Stress" },
  { value: "SUBSTANCE_USE", label: "Substance Use" },
  { value: "SUICIDAL_IDEATION", label: "Suicidal Ideation" },
  { value: "TEEN_VIOLENCE", label: "Teen Violence" },
  { value: "TRANSGENDER", label: "Transgender" },
  { value: "TRAUMA_PTSD", label: "Trauma and PTSD" },
] as const;

export const ClinicalSpecialtyEnumToStr: Record<ClinicalSpecialty, string> = {
  [ClinicalSpecialty.ADOPTION]: "Adoption",
  [ClinicalSpecialty.ANGER_MANAGEMENT]: "Anger Management",
  [ClinicalSpecialty.ANXIETY]: "Anxiety",
  [ClinicalSpecialty.BEHAVIORAL_ISSUES]: "Behavioral Issues",
  [ClinicalSpecialty.BIPOLAR_DISORDER]: "Bipolar Disorder",
  [ClinicalSpecialty.BISEXUAL]: "Bisexual",
  [ClinicalSpecialty.BODY_POSITIVITY]: "Body Positivity",
  [ClinicalSpecialty.CHRONIC_ILLNESS]: "Chronic Illness",
  [ClinicalSpecialty.CODEPENDENCY]: "Codependency",
  [ClinicalSpecialty.COPING_SKILLS]: "Coping Skills",
  [ClinicalSpecialty.DEPRESSION]: "Depression",
  [ClinicalSpecialty.DISSOCIATIVE_DISORDERS]: "Dissociative Disorders (DID)",
  [ClinicalSpecialty.DIVORCE]: "Divorce",
  [ClinicalSpecialty.DOMESTIC_ABUSE]: "Domestic Abuse",
  [ClinicalSpecialty.DOMESTIC_VIOLENCE]: "Domestic Violence",
  [ClinicalSpecialty.DUAL_DIAGNOSIS]: "Dual Diagnosis",
  [ClinicalSpecialty.EDUCATION_LEARNING_DISABILITIES]:
    "Education and Learning Disabilities",
  [ClinicalSpecialty.EMOTIONAL_DISTURBANCE]: "Emotional Disturbance",
  [ClinicalSpecialty.FAMILY_CONFLICT]: "Family Conflict",
  [ClinicalSpecialty.FAITH_BASED_COUNSELING]: "Faith-based Counseling",
  [ClinicalSpecialty.FOSTER_CARE]: "Foster Care",
  [ClinicalSpecialty.GRIEF]: "Grief",
  [ClinicalSpecialty.INTELLECTUAL_DISABILITY]: "Intellectual Disability",
  [ClinicalSpecialty.IMPULSE_CONTROL_DISORDERS]: "Impulse Control Disorders",
  [ClinicalSpecialty.INFIDELITY]: "Infidelity",
  [ClinicalSpecialty.LESBIAN]: "Lesbian",
  [ClinicalSpecialty.LGBTQ]: "LGBTQ+",
  [ClinicalSpecialty.MOOD_DISORDERS]: "Mood Disorders",
  [ClinicalSpecialty.NARCISSISTIC_PERSONALITY]:
    "Narcissistic Personality (NPD)",
  [ClinicalSpecialty.NON_BINARY]: "Non-Binary",
  [ClinicalSpecialty.OCD]: "Obsessive–Compulsive (OCD)",
  [ClinicalSpecialty.ODD]: "Oppositional Defiance (ODD)",
  [ClinicalSpecialty.PARENTING]: "Parenting",
  [ClinicalSpecialty.PEER_RELATIONSHIPS]: "Peer Relationships",
  [ClinicalSpecialty.PERSONALITY_DISORDERS]: "Personality Disorders",
  [ClinicalSpecialty.PSYCHOSIS]: "Psychosis",
  [ClinicalSpecialty.PSYCHOTIC_DISORDER]: "Psychotic Disorder",
  [ClinicalSpecialty.QUEER]: "Queer",
  [ClinicalSpecialty.RACIAL_IDENTITY]: "Racial Identity",
  [ClinicalSpecialty.RELATIONSHIP_ISSUES]: "Relationship Issues",
  [ClinicalSpecialty.SCHIZOPHRENIA]: "Schizophrenia",
  [ClinicalSpecialty.SCHOOL_ISSUES]: "School Issues",
  [ClinicalSpecialty.SELECTIVE_MUTISM]: "Selective Mutism",
  [ClinicalSpecialty.SELF_ESTEEM]: "Self Esteem",
  [ClinicalSpecialty.SELF_HARMING]: "Self-Harming",
  [ClinicalSpecialty.SEXUAL_ABUSE]: "Sexual Abuse",
  [ClinicalSpecialty.SEXUAL_TRAUMA]: "Sexual Trauma",
  [ClinicalSpecialty.SPIRITUALITY]: "Spirituality",
  [ClinicalSpecialty.SPORTS_PERFORMANCE]: "Sports Performance",
  [ClinicalSpecialty.STRESS]: "Stress",
  [ClinicalSpecialty.SUBSTANCE_USE]: "Substance Use",
  [ClinicalSpecialty.SUICIDAL_IDEATION]: "Suicidal Ideation",
  [ClinicalSpecialty.TEEN_VIOLENCE]: "Teen Violence",
  [ClinicalSpecialty.TRANSGENDER]: "Transgender",
  [ClinicalSpecialty.TRAUMA_PTSD]: "Trauma and PTSD",
};

export const ReligionEnumToStr: Record<Religion, string> = {
  [Religion.AGNOSTIC]: "Agnostic",
  [Religion.ATHEIST]: "Atheist",
  [Religion.BUDDHIST]: "Buddhist",
  [Religion.CHRISTIAN_CATHOLIC]: "Christian (Catholic)",
  [Religion.CHRISTIAN_PROTESTANT]: "Christian (Protestant)",
  [Religion.CHRISTIAN_OTHER]: "Christian (Other)",
  [Religion.HINDU]: "Hindu",
  [Religion.JEWISH]: "Jewish",
  [Religion.JAIN]: "Jain",
  [Religion.MUSLIM]: "Muslim",
  [Religion.SIKH]: "Sikh",
  [Religion.SPIRITUAL_BUT_NOT_RELIGIOUS]: "Spiritual but not Religious",
  [Religion.UNITARIAN_UNIVERSALIST]: "Unitarian Universalist",
  [Religion.OTHER]: "Other",
};

export const LGBTQIdentityEnumToStr: Record<LGBTQIdentity, string> = {
  [LGBTQIdentity.GAY]: "Gay",
  [LGBTQIdentity.LESBIAN]: "Lesbian",
  [LGBTQIdentity.BISEXUAL]: "Bisexual",
  [LGBTQIdentity.NON_BINARY]: "Non-Binary",
  [LGBTQIdentity.TRANSGENDER]: "Transgender",
  [LGBTQIdentity.QUEER]: "Queer",
  [LGBTQIdentity.OTHER]: "Other",
};

// Enum mappings for engagement and interest assessment
export const EngagementVerificationTypeEnumToStr: Record<
  EngagementVerificationType,
  string
> = {
  [EngagementVerificationType.YES]: "Yes",
  [EngagementVerificationType.NO]: "No",
  [EngagementVerificationType.PARTIAL_DISCUSSION]: "Partial Discussion",
};

export const InterestAssessmentTypeEnumToStr: Record<
  InterestAssessmentType,
  string
> = {
  [InterestAssessmentType.VERY_INTERESTED]: "Very Interested",
  [InterestAssessmentType.SOMEWHAT_INTERESTED]: "Somewhat Interested",
  [InterestAssessmentType.NEUTRAL]: "Neutral",
  [InterestAssessmentType.NOT_VERY_INTERESTED]: "Not Very Interested",
  [InterestAssessmentType.NOT_INTERESTED]: "Not Interested",
  [InterestAssessmentType.NOT_SURE]: "Not Sure",
};

export const TherapyTypeEnumToStr: Record<TherapyType, string> = {
  [TherapyType.INTAKE]: "Intake",
  [TherapyType.INDIVIDUAL]: "Individual therapy",
  [TherapyType.GROUP]: "Group therapy",
  [TherapyType.FAMILY]: "Family therapy",
  [TherapyType.MEDICATION_MANAGEMENT]: "Medication management",
};

export const ReferralRiskFactorEnumToStr: Record<ReferralRiskFactor, string> = {
  [ReferralRiskFactor.HOUSING_INSECURITY]: "Housing insecurity",
  [ReferralRiskFactor.FOOD_INSECURITY]: "Food insecurity",
  [ReferralRiskFactor.LIMITED_TECHNOLOGY_ACCESS]: "Limited technology access",
  [ReferralRiskFactor.FAMILY_SEPARATION_OR_DIVORCE]:
    "Family separation / divorce",
  [ReferralRiskFactor.LIMITED_TRANSPORTATION_ACCESS]:
    "Limited transportation access",
};

export const StudentThereapistRelationTypeEnumToStr: Record<
  StudentThereapistRelationType,
  string
> = {
  [StudentThereapistRelationType.PRIMARY]: "Primary",
  [StudentThereapistRelationType.SUPERVISING]: "Supervising",
};

export const DosageUnitEnumToStr: Record<DosageUnit, string> = {
  MG: "mg",
  MCG: "mcg",
  MCG_ML: "mcg/ml",
  G: "g",
  ML: "ml",
};

export const MedicationFormEnumToStr: Record<MedicationForm, string> = {
  TABLET: "Tablet",
  CAPSULE: "Capsule",
  SOLUTION: "Solution",
  SUSPENSION: "Suspension",
  PATCH: "Patch",
  LOZENGE: "Lozenge",
  INJECTION: "Injection",
};

export const FrequencyUnitEnumToStr: Record<FrequencyUnit, string> = {
  PER_DAY: "per day",
  PER_WEEK: "per week",
  PER_MONTH: "per month",
  AS_NEEDED: "as needed",
};

export const CallTypeEnumToStr: Record<CallType, string> = {
  [CallType.CONSULT]: "Consult",
  [CallType.INBOUND]: "Inbound",
  [CallType.OUTBOUND]: "Outbound",
};

export const CallOutcomeEnumToStr: Record<CallOutcome, string> = {
  [CallOutcome.MISSED]: "Missed",
  [CallOutcome.ANSWERED]: "Answered",
  [CallOutcome.OTHER]: "Other",
};

export const NoConversionReasonEnumToStr: Record<NoConversionReason, string> = {
  [NoConversionReason.NO_INSURANCE_ON_HAND]: "No insurance on hand",
  [NoConversionReason.NOT_A_GOOD_TIME]: "Not a good time",
  [NoConversionReason.OUT_OF_NETWORK]: "Out of network",
  [NoConversionReason.STILL_CONSIDERING]: "Still considering",
  [NoConversionReason.WANT_IN_PERSON]: "Want in person",
  [NoConversionReason.LANGUAGE_BARRIER]: "Language barrier",
  [NoConversionReason.ALREADY_IN_CARE]: "Already in care",
  [NoConversionReason.TECH_ISSUES]: "Technical issues",
  [NoConversionReason.RESISTANCE_TO_THERAPY_GUARDIAN]:
    "Guardian resistance to therapy",
  [NoConversionReason.RESISTANCE_TO_THERAPY_STUDENT]:
    "Student resistance to therapy",
  [NoConversionReason.COPAY_COST]: "Copay/cost",
  [NoConversionReason.WRONG_PHONE_NUMBER]: "Wrong phone number",
  [NoConversionReason.INACTIVE_INSURANCE]: "Inactive insurance",
  [NoConversionReason.OTHER]: "Other",
};

// Moved to `@marble/core` so the duplicate-intake cron can say how each
// intake was booked in its Slack alert; re-exported here because the app
// reaches for it through `@/lib/enumMappings`.
export { BookingSourceDescription } from "@marble/core/modules/appointments/constants";

export const ConfirmationSourceDescription: Record<ConfirmationSource, string> =
  {
    [ConfirmationSource.UNKNOWN]: "Unknown confirmation method",
    [ConfirmationSource.MESSAGE]: "Confirmed by client via SMS text message",
    [ConfirmationSource.PATIENT_PORTAL]:
      "Confirmed by client through the patient portal appointment page",
    [ConfirmationSource.ADMIN]: "Confirmed manually by a Marble admin",
    [ConfirmationSource.ONBOARDING]:
      "Confirmed automatically upon onboarding completion",
    [ConfirmationSource.VOICE_IVR]:
      "Confirmed by client pressing 1 on the voice IVR confirmation call",
    [ConfirmationSource.HEADWAY_SETUP]:
      "Confirmed automatically when Headway insurance setup completed",
  };

export const CaseStatusEnumToStr: Record<CaseStatus, string> = {
  [CaseStatus.OPEN_PENDING_SUCCESS]: "Open - Pending Success",
  [CaseStatus.OPEN_PENDING_OPS]: "Open - Pending Ops",
  [CaseStatus.OPEN_PENDING_EXTERNAL]: "Open - Pending External",
  [CaseStatus.OPEN_PENDING_HORATIO]: "Open - Pending Horatio",
  [CaseStatus.OPEN_PENDING_CONTRACTOR]: "Open - Pending Contractor",
  [CaseStatus.CLOSED]: "Closed",
};

export const CaseTypeEnumToStr: Record<CaseType, string> = {
  [CaseType.CARE_COORDINATION]: "Care Coordination",
  [CaseType.ONBOARDING_ISSUE]: "Onboarding Issue",
  [CaseType.CARE_GAP_RE_ENGAGEMENT]: "Care Gap / Re-engagement",
  [CaseType.PROVIDER_REMATCH]: "Provider Rematch",
  [CaseType.INSURANCE_ISSUE]: "Insurance Issue",
  [CaseType.SAFETY_CONCERN]: "Safety Concern",
  [CaseType.SCHEDULE_CHANGE]: "Schedule Change",
  [CaseType.CARE_TERMINATION]: "Care Termination",
  [CaseType.AWAITING_CLIENT_HEADWAY_SETUP]: "Awaiting Client Headway Setup",
  [CaseType.HEADWAY_MANUAL_INSURANCE_VERIFICATION]:
    "Headway Manual Insurance Verification",
  [CaseType.OTHER]: "Other",
  [CaseType.HEADWAY_UNBILLABLE_APPOINTMENT]: "Headway Unbillable Appointment",
  [CaseType.MEDICAID_ID_VERIFICATION]: "Medicaid ID Verification",
  [CaseType.PAYER_CRED_APPROVAL]: "Payer Credentialing Approval",
  [CaseType.PAYER_CRED_FOLLOW_UP]: "Payer Credentialing Follow-up",
  [CaseType.STATE_LICENSE_RENEWAL]: "State License Renewal",
  [CaseType.MEDICAID_ID_RENEWAL]: "Medicaid ID Renewal",
  [CaseType.PAYER_REQUESTED_CRED_APP_FOLLOW_UP]:
    "Payer Requested Cred App Follow Up",
  [CaseType.INSURANCE_TRANSCRIPTION]: "Insurance Transcription",
};

export const UserTaskTypeEnumToStr: Record<TaskType, string> = {
  [TaskType.COLD_CALL]: "Call 1",
  [TaskType.COLD_CALL_2]: "Call 2",
  [TaskType.COLD_CALL_3]: "Call 3",
  [TaskType.COLD_CALL_4_OR_GREATER]: "Call 4+",
  [TaskType.CALL_FOLLOW_UP]: "Call Follow-up",
  [TaskType.TEXT_FOLLOW_UP]: "Text Follow-up",
  [TaskType.RESOLVE_PHONE_NUMBER]: "Resolve Phone Number",
  [TaskType.LANGUAGE_CALL_FOLLOW_UP]: "Language Call Follow-Up",
  [TaskType.INTAKE_CANCELLED]: "Intake Cancelled",
  [TaskType.INTAKE_NO_SHOWED]: "Intake No-Showed",
  [TaskType.MISSING_CONSENT]: "Missing Consent",
  [TaskType.SCHEDULE_IND]: "Schedule Individual Therapy",
  [TaskType.SCHEDULE_FAMILY]: "Schedule Family Therapy",
  [TaskType.SCHEDULE_GROUP]: "Schedule Group Therapy",
  [TaskType.SCHEDULE_MED_MGMT_INTAKE]: "Schedule Medication Management Intake",
  [TaskType.RESCHEDULE_INDIVIDUAL]: "Reschedule Individual",
  [TaskType.RESCHEDULE_FAMILY]: "Reschedule Family Therapy",
  [TaskType.RESCHEDULE_GROUP]: "Reschedule Group Therapy",
  [TaskType.RESCHEDULE_MED_MGMT]: "Reschedule Medication Management",
  [TaskType.REMATCH_REQUEST]: "Rematch Request",
  [TaskType.MANUAL_INSURANCE_DISCOVERY]: "Manual Insurance Discovery",
  [TaskType.INVESTIGATE_OON_INSURANCE]: "Investigate OON Insurance",
  [TaskType.INVESTIGATE_INELIGIBLE_INSURANCE]:
    "Investigate Ineligible Insurance",
  [TaskType.INVESTIGATE_COVERAGE_DENIAL]: "Investigate Coverage Denial",
  [TaskType.INVESTIGATE_COB_DENIAL]:
    "Investigate Coordination of Benefits Denial",
  [TaskType.CASE_TASK]: "Case Task",
  [TaskType.CHECK_CLIENT_HEADWAY_SETUP]: "Check Client Headway Setup",
  [TaskType.SUBMIT_CLIENT_TO_HEADWAY]: "Submit Client to Headway",
  [TaskType.CHECK_HEADWAY_MANUAL_INSURANCE_VERIFICATION]:
    "Check Headway Manual Insurance Verification",
  [TaskType.CHECK_HEADWAY_UNBILLABLE_APPOINTMENT]:
    "Check Headway Unbillable Appointment",
  [TaskType.SUBMIT_INSURANCE_CLAIM_TO_HEADWAY]:
    "Submit Insurance Claim to Headway",
  [TaskType.SUBMIT_NO_SHOW_FEE_TO_HEADWAY]: "Submit No-Show Fee to Headway",
  [TaskType.REVERSE_NO_SHOW_FEE_IN_HEADWAY]: "Reverse No-Show Fee in Headway",
  [TaskType.CHECK_MEDICAID_ID_STATUS]: "Check Medicaid ID Status",
  [TaskType.UPLOAD_CRED_INFO_TO_CAQH]: "Upload Credentialing Info to CAQH",
  [TaskType.SUBMIT_CRED_APPLICATION_FOR_PAYER]:
    "Submit Credentialing Application for Payer",
  [TaskType.CHECK_PAYER_FOR_CRED_APPROVAL]:
    "Check Payer for Credentialing Approval",
  [TaskType.FOLLOW_UP_ON_PAYER_CRED_APPROVAL]:
    "Follow-Up on Payer Credentialing Approval",
  [TaskType.CAQH_REATTESTATION]: "CAQH Re-Attestation",
  [TaskType.LICENSE_RENEWAL_CHECK]: "State License Renewal Check",
  [TaskType.MEDICAID_ID_RENEWAL_CHECK]: "Medicaid ID Renewal Check",
  [TaskType.THERAPIST_LICENSE_ISSUE_DATES]: "Update License Information",
  [TaskType.CHECK_ON_PAYER_REQUESTED_CRED_APP_FOLLOW_UP]:
    "Check on Payer Requested Cred App Follow Up",
  [TaskType.INITIAL_CLIENT_COB_CHECK]: "Initial Client COB Check",
  [TaskType.INSURANCE_TRANSCRIPTION_FOLLOW_UP]:
    "Insurance Transcription Follow-up",
  [TaskType.COB_ERROR_SWITCH_TO_PRIMARY_PAYER]:
    "COB error: Switch to primary payer",
  [TaskType.COUNSELOR_FAMILY_UNRESPONSIVE]: "Family Unresponsive",
  [TaskType.COUNSELOR_MISSED_SESSIONS]: "Missed Sessions",
  [TaskType.COUNSELOR_INTAKE_NOT_SCHEDULED]: "Intake Not Scheduled",
  [TaskType.RECONNECT_EXTERNAL_CALENDAR]: "Reconnect External Calendar",
  [TaskType.ACCEPT_ZOOM_INVITATION]: "Accept Marble Zoom Invitation",
  [TaskType.THERAPIST_CONTACT_INFO]: "Update Contact Information",
  [TaskType.THERAPIST_SCHEDULING_PREFERENCES]: "Update Scheduling Preferences",
  [TaskType.THERAPIST_COMPLETE_PROFILE]: "Complete Profile",
  [TaskType.THERAPIST_NPS_SURVEY]: "Quarterly Provider Survey",
  [TaskType.THERAPIST_SCHOOL_COUNSELOR_STATUS]: "School Counselor Status",
  [TaskType.COUNSELOR_STUDENT_DISCHARGED_ATTENDANCE]:
    "Student Discharged for Attendance",
  [TaskType.COUNSELOR_STUDENT_CARE_GAP]: "Student in Care Gap",
  [TaskType.COUNSELOR_STUDENT_REFERRED_OUT_ACUITY]:
    "Student Referred Out for Safety (SI/HI)",
  [TaskType.AZ_SDOH_REFERRAL]: "Refer Family for SDoH Needs",
  [TaskType.AZ_FAMILY_SUPPORT_REFERRAL]: "Refer Family to Family Support",
  [TaskType.AZ_HIGH_NEEDS_CASE_MANAGEMENT_REFERRAL]:
    "Refer Family to High Needs Case Management",
  [TaskType.AZ_TRBHA_TREATMENT_PLAN_INPUT]:
    "Ask TRBHA/T-ALTCS Case Manager for Treatment Plan Input",
  [TaskType.AZ_SEND_INTAKE_RECORD_TO_PROVIDER]:
    "Send Intake Record to Provider in Rovicare",
  [TaskType.AZ_REQUEST_PROVIDER_EMAIL]:
    "Call Provider for Email to Send Intake Record",
  [TaskType.AZ_CASE_MANAGER_INTRODUCTION]: "Introduce Yourself to Case Manager",
  [TaskType.AZ_CFT_MEETING_INVITE_REQUEST]:
    "Ask CFT Facilitator for an Invite to CFT Meetings",
  [TaskType.AZ_CFT_HEALTH_HOME_REQUEST]: "Ask Health Home to Run CFT",
  [TaskType.AZ_CFT_SETUP_REQUEST]: "Email Providers Team to Set Up CFT",
  [TaskType.AZ_SDOH_REFERRAL_FOLLOW_UP]: "Follow Up on SDoH Referral",
  [TaskType.AZ_FAMILY_SUPPORT_REFERRAL_FOLLOW_UP]:
    "Follow Up on Family Support Referral",
  [TaskType.AZ_HIGH_NEEDS_CASE_MANAGEMENT_FOLLOW_UP]:
    "Follow Up on High Needs Case Management Referral",
  [TaskType.AZ_SI_HIGHER_LEVEL_OF_CARE_REFERRAL]:
    "Refer to a Higher Level of Care",
  [TaskType.AZ_SI_HIGHER_LEVEL_OF_CARE_FOLLOW_UP]:
    "Follow Up on Higher Level of Care Referral",
  [TaskType.AZ_MISSED_APPOINTMENT_OUTREACH]:
    "Call to Book After a Missed Session",
};

export const UserTaskSubtypeEnumToStr: Record<TaskSubtype, string> = {
  [TaskSubtype.PROVIDER_CANCELLED]: "Provider Cancelled",
  [TaskSubtype.PROVIDER_NO_SHOW]: "Provider No Show",
  [TaskSubtype.CLIENT_CANCELLED]: "Client Cancelled",
  [TaskSubtype.MARBLE_CANCELLED]: "Marble Cancelled",
  [TaskSubtype.AUTO_CANCELLED]: "Auto Cancelled",
  [TaskSubtype.ONBOARDING]: "Onboarding",
};

export const ClinicalClientNeedEnumToStr: Record<ClinicalClientNeed, string> = {
  [ClinicalClientNeed.AUTISM_SPECTRUM_DISORDER]: "Autism Spectrum Disorder",
  [ClinicalClientNeed.ADHD]: "ADHD",
  [ClinicalClientNeed.EATING_DISORDERS]: "Eating Disorders",
};

export const CarePlanStatusToStr: Record<CarePlanStatus, string> = {
  [CarePlanStatus.INTERESTED]: "Interested",
  [CarePlanStatus.NEEDS_SCHEDULING]: "Needs Scheduling",
  [CarePlanStatus.SCHEDULED]: "Scheduled",
  [CarePlanStatus.COMPLETED]: "Completed",
  [CarePlanStatus.OPTED_OUT]: "Opted Out",
};
